import {
  Component,
  OnInit,
  computed,
  inject,
  signal,
  viewChild,
  ElementRef,
  PLATFORM_ID,
  Inject,
  OnDestroy
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from "@angular/common";
import { ResolutionService } from '../shared/resolution.service';
import { SignalsStoreService } from '../shared/signals-store.service';
import { Meta, Title } from '@angular/platform-browser';
import { environment } from '../../environments/environment';
import { handleImageError } from '../shared/common/utils';
import KeenSlider, { KeenSliderInstance } from 'keen-slider';
import { RouterLink } from '@angular/router';
import { FooterComponent } from "../shared/footer/footer.component";
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { BlogService } from '../pages/blog/blog.service';
import { delay, finalize, tap } from 'rxjs';
import { PostComponent } from '../pages/blog/post/post.component';
import { ProductsService } from '../product/products.service';
import { ProductCardV2Component } from '../shared/product-card-v2/product-card-v2.component';

@Component({
  selector: 'app-home',
  imports: [
    CommonModule,
    RouterLink,
    FooterComponent,
    PostComponent,
    ProductCardV2Component,
    MatTabGroup,
    MatTab
  ],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss'
})
export class HomeComponent implements OnInit, OnDestroy {

  //#region Services

  #resolutionService = inject(ResolutionService);

  #signalsStoreService = inject(SignalsStoreService);

  #meta = inject(Meta);

  #title = inject(Title);

  #blogService = inject(BlogService);

  #productsService = inject(ProductsService);

  //#endregion

  //#region Properties

  productSlider = viewChild.required<ElementRef>('productSlider');

  slider = viewChild.required<ElementRef>('slider');

  hasSession = computed(() => this.#signalsStoreService.hasSession());

  isSessionLoaded = computed(() => this.#signalsStoreService.isSessionLoaded());

  isMobile = computed(() => this.#resolutionService.isMobile());

  slides = [
    {
      title: 'REGENERATIVE',
      boldSubtitle: '& SUSTAINABLE',
      subtitle: '',
    },
    {
      title: 'FARM FRESH',
      boldSubtitle: '',
      subtitle: 'Fruits & Vegetables',
    },
    {
      title: 'EAT HEALTY',
      boldSubtitle: '',
      subtitle: 'Be Healthy',
    },
    {
      title: 'REGENERATIVE',
      boldSubtitle: '& SUSTAINABLE',
      subtitle: '',
    },
  ];

  sliderObj: KeenSliderInstance | any = null;

  productSliderObj: KeenSliderInstance | any = null;

  dotHelper = signal<number[]>([]);

  currentSlide = 0;

  currentTestimonialSlide = computed(() => this.testimonialInitIndex() + 1);

  howItWorks = signal([
    {
      title: 'Sign Up',
      description: 'Shopping for local, farm fresh food has never been so quick & easy.',
      extra: 'No subscriptions, no obligations!'
    },
    {
      title: 'Fill your Basket',
      description: 'The areas finest dairy, eggs, meat, fish, vegetables & pantry items are all here for you to choose from.'
    },
    {
      title: 'Delivered',
      description: 'Home delivery, Popup Pickup Location or Farm Store.'
    }
  ]);

  whyChooseUs = signal([
    {
      title: 'Natural Production',
      img: '/assets/images/landing/home/why-choose-us/natural-production.webp',
      description: 'Regenerative & sustainable methods, Pasture-raised animals, non-GMO feeds.'
    },
    {
      title: 'Direct Delivery',
      img: '/assets/images/landing/home/why-choose-us/direct-shipping.webp',
      description: 'Our Carlton Farms trucks and drivers deliver from our farm to your door or Popup Pickup location. No 3rd party delivery.'
    },
    {
      title: 'Quality That Speaks for Itself',
      img: '/assets/images/landing/home/why-choose-us/certified-quality.webp',
      description: 'Local farm-fresh food has benefits for our health, our communities, and our environment, but maybe the most noticeable difference is the taste. Try it for yourself.'
    },
    {
      title: 'We Support the Local Food Community',
      img: '/assets/images/landing/home/why-choose-us/support-local.webp',
      description: 'In addition to the products we produce on-farm, we also partner with other like-minded local farmers to provide a more diverse offering. Specific producers are communicated transparently for each item.'
    },
  ]);

  categories = signal([
    {
      name: 'Dairy & Eggs',
      icon: 'dairy-eggs.webp',
      link: ['/shop/dairy-and-eggs']
    },
    {
      name: 'Meat & Seafood',
      icon: 'meat-seafood.webp',
      link: ['/shop/meat-and-seafood']
    },
    {
      name: 'Fruits & Vegatables',
      icon: 'fruits-vegetables.webp',
      link: ['/shop/fruits-and-vegetables']
    },
    {
      name: 'Raw Honey',
      icon: 'raw-honey.webp',
      link: ['/shop/meat-and-seafood']
    },
    {
      name: 'Cultured Products',
      icon: 'cultured-products.webp',
      link: ['/shop/cultured-products']
    },
    {
      name: 'Pantry',
      icon: 'pantry.webp',
      link: ['/shop/pantry']
    },
    {
      name: 'Personal Care',
      icon: 'personal-care.webp',
      link: ['/shop/meat-and-seafood']
    },
    {
      name: 'Farm Gifts',
      icon: 'farm-gifts.webp',
      link: ['/shop/meat-and-seafood']
    },
  ]);

  testimonials = signal<any>([
    {
      author: 'Kevin Krautsack',
      comment: "Best beef and eggs around. Been buying from Chad for many years now. He is commited to excellence for sure.",
      type: 'Carlton Farms customer'
    },
    {
      author: 'David-Christ Kleiman',
      comment: "Beautiful farm! Beautiful family! Highly satisfied with all products we have purchased!",
      type: 'Carlton Farms customer'
    },
    {
      author: 'Hilary Quirk Galt',
      comment: "The fruits are amazing and they deliver it right to the door - so delicious!",
      type: 'Carlton Farms customer'
    },
    {
      author: 'Kevin Krautsack',
      comment: "Best beef and eggs around. Been buying from Chad for many years now. He is commited to excellence for sure.",
      type: 'Carlton Farms customer'
    },
    {
      author: 'David-Christ Kleiman',
      comment: "Beautiful farm! Beautiful family! Highly satisfied with all products we have purchased!",
      type: 'Carlton Farms customer'
    },
    {
      author: 'Hilary Quirk Galt',
      comment: "The fruits are amazing and they deliver it right to the door - so delicious!",
      type: 'Carlton Farms customer'
    },
    {
      author: 'John Ruggieri',
      comment: "The best little farm in Georgia for meat and dairy.",
      type: 'Carlton Farms customer'
    },
    {
      author: 'Wendy Hardin',
      comment: "Always a lot of fun to visit here and the farm fresh items in the store are wonderful also!",
      type: 'Carlton Farms customer'
    },
    {
      author: 'Lauren Richardson',
      comment: "Delicious milk and eggs!",
      type: 'Carlton Farms customer'
    },
    {
      author: 'Lyndsey Schappe',
      comment: "The absolute best meat. Super high quality, and great price. We love picking up our orders at the farm!",
      type: 'Carlton Farms customer'
    },
  ]);

  testimonialsInView = computed(() => this.#getTestimonialsInView());

  testimonialInitIndex = signal(4);

  testimonialLastIndex = signal(7);

  showNextButton = computed(() => !((this.testimonialLastIndex() - 2) >= (this.testimonials().length - 1)));

  showPrevButton = computed(() => this.testimonialInitIndex() >= 0);

  activities = signal([
    {
      title: 'On-Farm Store',
      descriptions: [
        "Come to our store and take home fresh produce straight from the farm.",
        "Exclusive products you'll only find here.",
        "Open Monday - Saturday 10-6.",
        "Purchase directly at the farm."
      ],
      buttonText: 'EXPLORE OUR STORE',
      img: 'on-farm-store.webp',
      route: '/pages/on-farm-store'
    },
    {
      title: 'Corn Maze',
      descriptions: [
        "Dare to walk through our corn maze, a unique experience for the whole family.",
        "Trails with different levels of difficulty.",
        "Spaces for photos and outdoor fun.",
        "Get lost in our 5-acre Corn Maze.",
        "Visit with the farm animals.",
        "Take a hay ride and pick out your pumpkins."
      ],
      buttonText: 'DISCOVER THE MAZE',
      img: 'corn-maze.webp',
      route: '/pages/corn-maze'
    },
    {
      title: 'School Field Trips',
      descriptions: [
        "During Spring or Fall.",
        "Educational field trips for students of all ages.",
        "Learn about farm life, sustainability and food production.",
        "Animal interaction and hands-on experiences."
      ],
      buttonText: 'ORGANIZE A SCHOOL VISIT',
      img: 'school-field-tips.webp',
      route: '/pages/school-field-trips'
    },
  ]);

  posts = signal<any>([]);

  discoverProducts = signal([
    {
      id: 552,
      name: 'Featured Products'
    },
    {
      id: 555,
      name: 'Farmer Specials'
    },
    {
      id: 556,
      name: 'Best Seller'
    }
  ]);

  products = signal<any[]>([]);

  loadingProducts = signal(true);

  //#endregion

  //#region Constructors

  constructor(@Inject(PLATFORM_ID) private platformId: any) { }

  ngOnInit() {
    this.#setUpSEOTags();

    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => {
        this.#setUpCarousel();
      }, 1000);
      this.#getLatestPosts();
      this.#getProducts(this.discoverProducts()[0].id);
    }
  }

  ngOnDestroy() {
    if (this.sliderObj && this.sliderObj.destroy)
      this.sliderObj.destroy();
  }

  //#endregion

  //#region Methods

  handleImageError = handleImageError;

  #setUpSEOTags() {

    const { mainTitle, mainDescription, baseKeywords: keywords, baseURL, type } = environment.config.seo;
    const url = 'home';

    // Page title
    this.#title.setTitle(mainTitle);

    // Standart <meta> tag for SEO
    this.#meta.addTag({ name: 'description', content: mainDescription });
    this.#meta.addTag({ name: 'keywords', content: keywords.join(', ') });

    // Open Graph for social networks:
    this.#meta.addTag({ property: 'og:title', content: mainTitle });
    this.#meta.addTag({ property: 'og:description', content: mainDescription });
    this.#meta.addTag({ property: 'og:url', content: `${baseURL}${url}` });
    this.#meta.addTag({ property: 'og:type', content: type });
  }

  #setUpCarousel() {

    if (!(this.slider()?.nativeElement))
      return;

    this.sliderObj = new KeenSlider(this.slider().nativeElement, {
      initial: this.currentSlide,
      renderMode: 'performance',
      loop: this.slides.length > 1,
      drag: false,
      slideChanged: s => this.currentSlide = s.track.details.rel
    }, [
      (slider) => {
        let timeout: NodeJS.Timeout | null = null;
        let mouseOver = false
        function clearNextTimeout() {
          if (timeout)
            clearTimeout(timeout);
        }
        function nextTimeout() {

          if (timeout)
            clearTimeout(timeout);

          if (mouseOver) return
          timeout = setTimeout(() => {
            slider.next()
          }, 1000 * 5)
        }
        slider.on("created", () => {
          slider.container.addEventListener("mouseover", () => {
            mouseOver = true
            clearNextTimeout()
          })
          slider.container.addEventListener("mouseout", () => {
            mouseOver = false
            nextTimeout()
          })
          nextTimeout()
        })
        slider.on("dragStarted", clearNextTimeout)
        slider.on("animationEnded", nextTimeout)
        slider.on("updated", nextTimeout)
      }
    ]);

    if (this.sliderObj.track?.details?.slides?.length) {
      this.dotHelper.set([
        ...Array(this.sliderObj.track.details.slides.length)
      ]);
    }
  }

  next() {
    if (!this.showNextButton()) return;
    this.testimonialInitIndex.update(prev => prev + 1);
    this.testimonialLastIndex.update(prev => prev + 1);
  }

  prev() {
    if (!this.showPrevButton()) return;
    this.testimonialInitIndex.update(prev => prev - 1);
    this.testimonialLastIndex.update(prev => prev - 1);
  }

  #getTestimonialsInView() {

    if (this.testimonialInitIndex() < 0) {
      return [null, ...this.testimonials().slice(0, this.testimonialLastIndex())];
    }

    return this.testimonials().slice(this.testimonialInitIndex(), this.testimonialLastIndex())
  }

  #getLatestPosts() {

    this.#blogService.page.set(0);
    this.#blogService.size.set(3);

    this.#blogService
      .getAll()
      .pipe(
        finalize(() => {
          this.#blogService.page.set(0);
          this.#blogService.size.set(10);
        }),
        tap(response => this.posts.set(response.data?.blogs ?? []))
      )
      .subscribe();
  }

  onTabChange(event: any) {
    this.#getProducts(this.discoverProducts()[event.index].id);
  }

  #getProducts(id: number) {

    this.loadingProducts.set(true);
    this.products.set([]);

    this.#productsService
      .getProductsByCategory(id)
      .pipe(
        finalize(() => this.loadingProducts.set(false)),
        tap(response => this.products.set(response.data?.products ?? [])),
        delay(1000),
        tap(() => this.loadingProducts.set(false)),
        delay(1000),
        tap(() => this.#initProductSlider())
      )
      .subscribe();
  }

  #initProductSlider() {

    if (this.productSliderObj)
      this.productSliderObj.destroy();

    this.productSliderObj = new KeenSlider(this.productSlider().nativeElement, {
      loop: false,
      mode: 'free',
      slides: {
        perView: 'auto',
        spacing: 10,
      }
    });
  }

  //#endregion
}
